import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Tab, Tabs } from '@mui/material'
import { Add, FolderOutlined, FilterListOff } from '@mui/icons-material'
import { Button } from 'Common/Supplyvue-ui'
import { PageHeader } from 'Common/Layout/PageHeader'
import { Loader } from 'Common/Loader/Loader'
import { EmptyState } from 'Common/EmptyState/EmptyState'
import { CreateProjectModal } from 'Features/Project/CreateProjectModal/CreateProjectModal.view'
import {
  useProjectListData,
  useCreateProjectData,
  useDeleteProject,
  useUpdateProjectAccess,
} from './ProjectList.data'
import { Project } from './ProjectList.types'
import { toast } from 'react-hot-toast'
import { DeleteProjectModal } from 'Features/Project/DeleteProjectModal/DeleteProjectModal.view'
import { ShowPlansOptionType } from 'Common/Components/Common.types'
import { PROJECT_ACCESS_OPTIONS } from 'Common/Constants/Constants'

import { getUserEmail } from 'Features/Auth/auth'
import { projectAccessType } from '../ProjectCard/ProjectCard.types'
import { ScenarioAccessModal } from '../ScenarioAccessModal/ScenarioAccess.Modal'
import { ProjectTable } from './ProjectTable.view'

/** The three ownership filters, as tabs. Values are the old dropdown's. */
const FILTERS: { value: ShowPlansOptionType; label: string }[] = [
  {
    value: PROJECT_ACCESS_OPTIONS['Generated by me'] as ShowPlansOptionType,
    label: 'Created by me',
  },
  {
    value: PROJECT_ACCESS_OPTIONS['Not generated by me'] as ShowPlansOptionType,
    label: 'Created by others',
  },
  {
    value: PROJECT_ACCESS_OPTIONS['Generated by anyone'] as ShowPlansOptionType,
    label: 'All projects',
  },
]

const filterProjects = (
  option: ShowPlansOptionType,
  projects: Project[],
  userEmail: string | null
) => {
  if (option === PROJECT_ACCESS_OPTIONS['Generated by me'])
    return projects.filter((project) => project.email === userEmail)
  if (option === PROJECT_ACCESS_OPTIONS['Not generated by me'])
    return projects.filter((project) => project.email !== userEmail)
  return projects
}

/**
 * The home page (`/` and `/projects`): every project the user can see, as
 * a grid (`ProjectTable`), filtered by who created them.
 *
 * Layout follows the shell: a sticky PageHeader carrying every page action,
 * then the content column at 24px gutters. The ownership filter is a row of
 * tabs with counts, so it is visible which tab holds the work before opening
 * one; it was a 200px dropdown.
 */
export const ProjectListPage = () => {
  const navigate = useNavigate()
  const isOptimiserEnabled = localStorage.getItem('tenantId') === 'tst'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState('')

  const [showProjectsOption, setShowProjectsOption] = useState(
    PROJECT_ACCESS_OPTIONS['Generated by me'] as ShowPlansOptionType
  )
  const [userEmail, setUserEmail] = useState<null | string>(null)
  const [scenarioAccessModalOpen, setScenarioAccessModalOpen] = useState(false)
  const [scenarioAccessConfig, setScenarioAccessConfig] = useState<
    Record<string, 'public' | 'private'>
  >({})

  useEffect(() => {
    if (userEmail === null) {
      getUserEmail().then((email: string | undefined) => {
        setUserEmail(email ? email.toLocaleLowerCase() : '')
      })
    }
    return () => {
      setUserEmail(null)
    }
  }, [])

  const onCreateProjectSuccess = (projectId: string, message: string) => {
    handleModalClose()
    navigate(`projects/${projectId}`)
    toast.success(message)
  }
  const { isPending: isCreatingProject, mutate: createProject } =
    useCreateProjectData({ onCreateProjectSuccess })

  // Track the project Id's that are being deleted
  const [deleting, setDeleting] = useState<string[]>([])

  const onDeleteSuccess = (projectId: string) => {
    toast.success('Delete Success')
    setDeleting((prevState: string[]) => {
      return prevState.filter((project_id: string) => project_id !== projectId)
    })
  }
  const onDeleteError = () => {
    toast.error('Delete Failed')
  }

  const { mutate: deleteProject, isPending: isDeleting } = useDeleteProject({
    onDeleteSuccess,
    onDeleteError,
  })

  const { isLoading: isProjectListLoading, data: projectListData = [] } =
    useProjectListData()

  // Derived, not mirrored into state by an effect. The effect this replaces
  // skipped an empty list, so deleting the last project left its card showing.
  const filtered = useMemo(
    () =>
      Object.fromEntries(
        FILTERS.map((filter) => [
          filter.value,
          filterProjects(filter.value, projectListData, userEmail),
        ])
      ) as Record<ShowPlansOptionType, Project[]>,
    [projectListData, userEmail]
  )
  const selectedProjectData = filtered[showProjectsOption] ?? []

  const handleNewProjectClick = () => {
    navigate('/create-project')
  }

  const handleDeleteModalClose = () => {
    setProjectId(null)
    setProjectName('')
    setIsDeleteModalOpen(false)
  }

  const handleModalClose = () => {
    setProjectName('')
    setIsModalOpen(false)
    setErrorMessage('')
  }

  const handleProjectNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProjectName(event.target.value)
  }

  const handleCreateProject = () => {
    if (
      projectListData.find(
        (project: Project) =>
          project.projectName.toLowerCase() === projectName.toLowerCase()
      )
    ) {
      setErrorMessage('Project name already exists')
    } else {
      setErrorMessage('')
      createProject({ projectName })
    }
  }

  const handleDelete = (projectId: string, projectName: string) => {
    setIsDeleteModalOpen(true)
    setProjectName(projectName)
    setProjectId(projectId)
  }

  const handleDeleteConfirm = () => {
    if (projectId) {
      deleteProject({ projectId })
      setDeleting((prevState: string[]) => {
        return [...prevState, projectId]
      })
    }
    handleDeleteModalClose()
  }

  const handleProjectOpen = (projectId: string) => {
    navigate(`/projects/${projectId}`)
  }

  /***** Project access change (public/private .....) */
  const onProjectAccessUpdateSuccess = () => {
    toast.success('Project access updated successfully')
  }

  const onProjectAccessUpdateFail = () => {
    toast.error('Failed to update project access')
  }

  const {
    mutate: updateProjectAccess,
    isPending: isProjectAccessUpdating,
    variables: projectAccessVariables,
  } = useUpdateProjectAccess(
    onProjectAccessUpdateSuccess,
    onProjectAccessUpdateFail
  )

  const handlePlanAccessChangeClick = (
    projectId: string,
    projectAccess: projectAccessType
  ) => {
    if (projectAccess.projectAccess.visibility === 'public') {
      setScenarioAccessConfig(
        projectAccess.scenarioAccess as Record<string, 'public' | 'private'>
      )
      setProjectId(projectId)
      setScenarioAccessModalOpen(true)
    } else {
      updateProjectAccess({ projectId, projectAccess })
    }
  }

  const handleScenarioAccessApply = (
    scenarioConfig: Record<string, 'public' | 'private'>
  ) => {
    const projectAccess = {
      projectAccess: {
        visibility: 'public' as 'public' | 'private',
      },
      scenarioAccess: { ...scenarioConfig },
    }
    updateProjectAccess({
      projectId: projectId as string,
      projectAccess: projectAccess,
    })
    setScenarioAccessModalOpen(false)
  }
  /***** End project access */

  const hasProjects = projectListData.length > 0
  const count = projectListData.length

  const actions = (
    <>
      {isOptimiserEnabled && (
        <Button variant="outlined" onClick={() => navigate('/optimiser')}>
          Optimiser
        </Button>
      )}
      {hasProjects && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleNewProjectClick}
        >
          New project
        </Button>
      )}
    </>
  )

  const activeFilterLabel =
    FILTERS.find((filter) => filter.value === showProjectsOption)?.label ?? ''

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <PageHeader
        title="Projects"
        subtitle={
          isProjectListLoading
            ? undefined
            : `${count} ${count === 1 ? 'project' : 'projects'}`
        }
        actions={actions}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          flex: 1,
          px: '24px',
          pt: '8px',
          pb: '24px',
        }}
      >
        {isProjectListLoading ? (
          <Loader />
        ) : !hasProjects ? (
          <EmptyState
            icon={<FolderOutlined />}
            title="No projects yet"
            text="A project holds a data snapshot and the scenarios you model against it."
            actionText="Create project"
            handleClick={handleNewProjectClick}
          />
        ) : (
          <>
            <Tabs
              value={showProjectsOption}
              onChange={(_event, value: ShowPlansOptionType) =>
                setShowProjectsOption(value)
              }
              aria-label="Filter projects by creator"
            >
              {FILTERS.map((filter) => (
                <Tab
                  key={filter.value}
                  value={filter.value}
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {filter.label}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10.5,
                          fontWeight: 600,
                          px: '5px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--line-100)',
                          color: 'var(--ink-600)',
                        }}
                      >
                        {filtered[filter.value]?.length ?? 0}
                      </Box>
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {selectedProjectData.length === 0 ? (
              <EmptyState
                icon={<FilterListOff />}
                title={`No projects under “${activeFilterLabel}”`}
                text="Projects created by other people are under the other tabs."
                actionText="Show all projects"
                handleClick={() =>
                  setShowProjectsOption(
                    PROJECT_ACCESS_OPTIONS[
                      'Generated by anyone'
                    ] as ShowPlansOptionType
                  )
                }
              />
            ) : (
              <ProjectTable
                projects={selectedProjectData}
                userEmail={userEmail}
                deletingIds={deleting}
                isDeleting={isDeleting}
                accessUpdatingId={
                  isProjectAccessUpdating
                    ? (projectAccessVariables?.projectId ?? null)
                    : null
                }
                handleOpen={handleProjectOpen}
                handleDelete={handleDelete}
                handleAccessChange={handlePlanAccessChangeClick}
              />
            )}
          </>
        )}
      </Box>

      <CreateProjectModal
        isModalOpen={isModalOpen}
        handleModalClose={handleModalClose}
        heading="Create project"
        projectNameLabel="Enter project name"
        projectName={projectName}
        handleProjectNameChange={handleProjectNameChange}
        actionButtonLabel="Create"
        handleActionClick={handleCreateProject}
        isActionDisabled={!projectName || isCreatingProject}
        isLoading={isCreatingProject}
        errorMessage={errorMessage}
      />
      <DeleteProjectModal
        isModalOpen={isDeleteModalOpen}
        handleModalClose={handleDeleteModalClose}
        projectName={projectName}
        handleConfirmClick={handleDeleteConfirm}
      />
      <ScenarioAccessModal
        isModalOpen={scenarioAccessModalOpen}
        handleModalClose={() => setScenarioAccessModalOpen(false)}
        heading="Scenarios"
        isLoading={false}
        projectId={projectId as string}
        handleScenarioAccessApply={handleScenarioAccessApply}
        scenarioAccessConfig={scenarioAccessConfig}
      />
    </Box>
  )
}
